import { useMemo } from 'react';
import {
  differenceInYears,
  getDay,
  isWithinInterval,
  startOfToday,
} from 'date-fns';
import { WEEKDAYS } from '@/constants/weekdays';
import { getHotelRoomPrice } from '@/utils/estimates/hotel/computeHotelRoomPrice';
import { roundUpPrice } from '@/utils/circuits/prices/formatPrice';
import { BenefitType } from '@/types/benefits/benefitTypes';
import { CircuitDayFormData } from '@/types/circuits/CircuitDaysFormData';
import { SelectedEstimateOption } from '@/types/estimates/SelectedEstimateOptionType';
import { Room } from '@/utils/estimates/findAllRoomCombinations';
import { PriceWithFormula } from '@/utils/circuits/prices/computeBenefitPrices';
import { EstimateResponse } from '@/types/estimates/EstimateResponse';
import { toHotelPensionType } from '@/constants/hotelPension';
import {
  resolvePensionPricings,
  getHotelPensionPriceForDate,
} from '@/utils/estimates/hotel/computeHotelPensionPrice';

interface StandardBenefitNameWithPrice {
  name: string;
  price: PriceWithFormula | undefined;
  marginRate: number | undefined;
  currency: string;
}

interface UseAccommodationBenefitProps {
  standardAccommodation: StandardBenefitNameWithPrice | undefined;
  selectedOptions: Record<string, SelectedEstimateOption>;
  step: CircuitDayFormData['steps'][number];
  date: Date;
  adults: EstimateResponse['adults'];
  children: EstimateResponse['children'];
}

export const useAccommodationBenefit = ({
  standardAccommodation,
  selectedOptions,
  step,
  date,
  adults,
  children,
}: UseAccommodationBenefitProps) => {
  const nbChildren = useMemo(
    () =>
      children.filter(({ birthDate }) => {
        if (
          !step.accommodations.standardBenefit?.hotel ||
          !step.accommodations.standardBenefit?.hotel.acceptsChildPricing ||
          !step.accommodations.standardBenefit?.hotel.childAgeFrom ||
          !step.accommodations.standardBenefit?.hotel.childAgeTo ||
          !birthDate
        )
          return false;
        const age = differenceInYears(startOfToday(), birthDate);
        return (
          age <= step.accommodations.standardBenefit?.hotel.childAgeTo &&
          age >= step.accommodations.standardBenefit?.hotel.childAgeFrom
        );
      }).length,
    [children, step.accommodations.standardBenefit?.hotel],
  );

  const nbAdolescents = useMemo(
    () =>
      children.filter(({ birthDate }) => {
        if (
          !step.accommodations.standardBenefit?.hotel ||
          !step.accommodations.standardBenefit?.hotel.acceptsChildPricing ||
          !step.accommodations.standardBenefit?.hotel.secondChildAgeFrom ||
          !step.accommodations.standardBenefit?.hotel.secondChildAgeTo ||
          !birthDate
        )
          return false;
        const age = differenceInYears(startOfToday(), birthDate);
        return (
          age <= step.accommodations.standardBenefit?.hotel.secondChildAgeTo &&
          age >= step.accommodations.standardBenefit?.hotel.secondChildAgeFrom
        );
      }).length,
    [children, step.accommodations.standardBenefit?.hotel],
  );

  const nbAdults = useMemo(
    () => adults.length + children.length - nbChildren - nbAdolescents,
    [adults.length, children.length, nbAdolescents, nbChildren],
  );

  return useMemo(() => {
    if (!standardAccommodation) return undefined;
    if (!selectedOptions) return standardAccommodation;
    const selectedOption = selectedOptions[step.accommodations.id];
    if (!selectedOption) return standardAccommodation;
    const roomCombinations = selectedOption.roomCombination;
    if (!roomCombinations) return standardAccommodation;
    const hotelBenefit = step.accommodations.standardBenefit?.hotel;
    const hotelCategory = hotelBenefit?.HotelRoomCategory[0];
    if (!hotelCategory) return standardAccommodation;

    const selectedPensionType = selectedOption.hotelPensionType;
    const standardPensionStable =
      toHotelPensionType(hotelBenefit?.pricingPensionType ?? undefined) ??
      'SingleRoom';

    let pensionPrice = 0;
    if (selectedPensionType && selectedPensionType !== standardPensionStable) {
      const pensionType = toHotelPensionType(selectedPensionType);
      if (pensionType) {
        const pricings = resolvePensionPricings(
          hotelBenefit?.commonPensionPricings,
          hotelCategory.categoryPensionPricings,
          hotelBenefit?.areOtherPensionPricesCommon ?? false,
        );

        const priceForDate = getHotelPensionPriceForDate(
          pricings,
          pensionType,
          date,
        );
        if (priceForDate) {
          pensionPrice =
            priceForDate.adultPrice * nbAdults +
            priceForDate.childPrice * nbChildren +
            priceForDate.adolescentPrice * nbAdolescents;
        }
      }
    }

    const allRoomPrices = roomCombinations
      .map((room: Room) => {
        const allPrices = hotelCategory.pricings
          .filter((pricing) =>
            pricing.periods.some((period) => {
              if (
                isWithinInterval(date, {
                  start: period.startDate,
                  end: period.endDate,
                })
              ) {
                const dayIndex = (getDay(date) + 6) % 7;
                const dayName = WEEKDAYS[dayIndex];
                if (period.applicationDays.includes(dayName)) return true;
              }
              return false;
            }),
          )
          .map((pricing) => {
            return getHotelRoomPrice(
              hotelCategory,
              pricing,
              [room[0], room[1], room[2]],
              hotelBenefit.pricingType ?? 'byRoom',
              hotelBenefit.reducedPriceExists ?? false,
              hotelBenefit.isReduction ?? false,
            );
          });
        const minimumPrice = Math.min(
          ...allPrices.map((price) => price.minimumPrice),
        );
        return allPrices.find((price) => price.minimumPrice === minimumPrice);
      })
      .filter((pricing) => pricing !== undefined);
    const combinedPricing = allRoomPrices.reduce(
      (acc, roomPrice) => ({
        minimumPrice: acc.minimumPrice + roomPrice.minimumPrice,
        formula: acc.formula
          ? `${acc.formula} + ${roomPrice.formula}`
          : roomPrice.formula,
      }),
      { minimumPrice: 0, formula: '' },
    );

    // Add pension price to the combined pricing
    return {
      name: standardAccommodation.name,
      price: {
        minimumPrice: combinedPricing.minimumPrice + pensionPrice,
        formula: combinedPricing.formula
          ? pensionPrice > 0
            ? pensionPrice > 0
              ? `${combinedPricing.formula} + ${pensionPrice}`
              : combinedPricing.formula
            : combinedPricing.formula
          : pensionPrice.toString(),
      },
      marginRate: standardAccommodation.marginRate,
      currency: standardAccommodation.currency,
    };
  }, [
    date,
    nbAdolescents,
    nbAdults,
    nbChildren,
    selectedOptions,
    standardAccommodation,
    step.accommodations.id,
    step.accommodations.standardBenefit?.hotel,
  ]);
};

interface UseTransportBenefitProps {
  standardTransport: StandardBenefitNameWithPrice | undefined;
  selectedOptions: Record<string, SelectedEstimateOption>;
  step: CircuitDayFormData['steps'][number];
  isFirstDay: boolean;
}

export const useTransportBenefit = ({
  standardTransport,
  selectedOptions,
  step,
  isFirstDay,
}: UseTransportBenefitProps) => {
  return useMemo(() => {
    if (!standardTransport) return undefined;
    if (!selectedOptions) return standardTransport;
    const selectedOption = selectedOptions[step.transport.id];
    if (!selectedOption) return standardTransport;
    if (selectedOption.type !== BenefitType.VEHICLE) return standardTransport;
    const carRenalOptions = selectedOption.carCategoryOptions;
    if (!carRenalOptions) return standardTransport;
    if (!isFirstDay) return standardTransport;

    const benefitMarginRate = standardTransport.marginRate ?? 0;
    const carRentalOptionsPrice = Object.values(carRenalOptions)
      .flatMap((options) => options)
      .filter(({ isRequired }) => !isRequired) // Required options are already included in the circuit price
      .map(({ deltaPrice }) =>
        roundUpPrice(deltaPrice / (1 + benefitMarginRate / 100), true),
      )
      .reduce(
        (acc, optionPrice) => ({
          minimumPrice: (acc.minimumPrice ?? 0) + (optionPrice ?? 0),
          formula:
            acc.formula && acc.formula.length
              ? `${acc.formula} + ${optionPrice}`
              : optionPrice.toString(),
        }),
        { minimumPrice: 0, formula: '' },
      );
    const globalPriceWithFormula = {
      minimumPrice:
        (standardTransport.price?.minimumPrice ?? 0) +
        carRentalOptionsPrice.minimumPrice,
      formula: standardTransport.price?.formula
        ? `${standardTransport.price.formula} + ${carRentalOptionsPrice.formula}`
        : carRentalOptionsPrice.formula,
    };
    return {
      ...standardTransport,
      price: globalPriceWithFormula,
    };
  }, [isFirstDay, selectedOptions, standardTransport, step.transport.id]);
};
